import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import StateObserver, { user } from 'src/StateObserver';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import { PetSpeciesID } from 'src/replicant/pet';
import { getPetSpeciesConfig } from 'src/replicant/pet/pet.getters';
import Checkbox from '../components/shared/Checkbox';
import { getPet } from '../pet';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';

import { AssetGroup } from '@play-co/timestep-core';

const ui = uiConfig.buruburu.care.confirmation;
type CloseHandler = (
  result: { redo: false; discard: boolean } | { redo: true },
) => void;

export default class PopupRedoEvolutionChoice extends PopupBasic {
  private candidatesContainer: FlexBoxView;
  private checkboxes: Checkbox[] = [];
  private discard: boolean = false;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close({ redo: false, discard: this.discard }),
      width: uiConfig.popups.buruburu.width,
      height: 770,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_popup',
      },
      // Add an icon to mush the title to the right
      // This will be hidden by a bigger variant of it to match the mock
      titleIcon: {
        image: 'assets/ui/redoEvolution/evo_redo_pocketwatch.png',
        offsetX: -40,
        width: 60,
        height: 60,
      },
    });

    this.buttonClose.removeFromSuperview();

    const width = this.box.style.width;

    new ImageScaleView({
      superview: this.box,
      autoSize: true,
      image: 'assets/ui/redoEvolution/evo_redo_pocketwatch.png',
      zIndex: 1000,
      x: -10,
      y: -120,
    });

    new LangBitmapFontTextView({
      superview: this.box,
      x: 40,
      y: 80,
      width: width - 80,
      height: 40,
      align: 'center',
      verticalAlign: 'center',
      font: 'Body',
      color: '#BF8147',
      localeText: () => i18n('evolutionRedoConfirm.prompt'),
      size: 30,
    });

    this.candidatesContainer = new FlexBoxView({
      superview: this.box,
      x: 30,
      y: 140,
      width: width - 60,
      height: 500,
      flex: {
        flexDirection: 'column',
        justifyContent: 'space-evenly',
      },
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        y: 124,
        width: width - 80,
      }),
    );

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: 'Body',
      x: width / 2 - 128,
      y: 692,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('evolutionRedoConfirm.confirm'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close({ redo: false, discard: this.discard });
      },
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_ok',
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: 'Body',
      x: width / 2 + 128,
      y: 692,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('evolutionRedo.redo'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close({ redo: true });
      },
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_redo',
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/redoEvolution/evo_redo_pocketwatch.png',
      'assets/ui/encyclopedia/frames/costume_data_frame.png',
      'assets/ui/encyclopedia/frames/costume_image_frame.png',
      'assets/ui/gacha/icons/star.png',
    ]);
  }

  public init({ species }: { species: PetSpeciesID[] }) {
    super.init({
      title: i18n('evolutionRedo.title'),
    });

    species.forEach((species) => {
      this.createCandidate(species);
    });
  }

  public createCandidate(species: PetSpeciesID) {
    const config = getPetSpeciesConfig(user(), species);

    const checkbox = new Checkbox({
      isChecked: species === getPet().species,
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_select',
      },
    });
    this.checkboxes.push(checkbox);

    const button = new ButtonScaleView({
      superview: this.candidatesContainer,
      width: this.box.style.width - 80,
      height: 192,
      onClick: async () => {
        this.checkboxes.forEach((other) => {
          other.updateState(other === checkbox);
        });

        this.discard = species !== getPet().species;
      },
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_select',
      },
    });

    const container = new FlexBoxView({
      superview: button,
      width: button.style.width,
      height: button.style.height,
      flex: {
        alignItems: 'center',
        justifyContent: 'space-between',
      },
    });

    container.addSubview(checkbox);

    const dataFrame = new ImageScaleView({
      superview: container,
      width: this.box.style.width - 140,
      height: 192,
      image: 'assets/ui/encyclopedia/frames/costume_data_frame.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 30, right: 30 },
      },
    });

    const petFrame = new ImageScaleView({
      superview: dataFrame,
      width: 200,
      height: 162,
      x: 10,
      y: 10,
      image: 'assets/ui/encyclopedia/frames/costume_image_frame.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 30, right: 30 },
      },
    });

    new ImageScaleView({
      superview: petFrame,
      width: 146,
      height: 146,
      x: petFrame.style.width / 2,
      y: petFrame.style.height / 2,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: `assets/ui/species/linepet_${species}.png`,
    });

    const locale = StateObserver.getLocale() === 'ja' ? 'jp' : 'en';

    new LangBitmapFontTextView({
      superview: dataFrame,
      font: 'Title',
      size: 36,
      width: 190,
      height: 40,
      x: 230,
      y: 20,
      verticalAlign: 'center',
      align: 'left',
      localeText: () => config[`name_${locale}`].replace('ぶるぶる科', ''),
    });

    new LangBitmapFontTextView({
      superview: dataFrame,
      font: 'Body',
      size: 20,
      width: 190,
      height: 40,
      x: 230,
      y: 60,
      verticalAlign: 'center',
      align: 'center',
      localeText: () => i18n(`petBoost.${config.boost_type}.title`),
    });

    const tierContainer = new FlexBoxView({
      superview: dataFrame,
      width: 140,
      height: 30,
      x: 230,
      y: dataFrame.style.height - 60,
      flex: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
      },
    });

    for (let i = 0; i < config.tier; i++) {
      new ImageScaleView({
        superview: tierContainer,
        image: 'assets/ui/gacha/icons/star.png',
        width: 30,
        height: 30,
      });
    }
  }
}
